const DONE_SIGNAL = '[DONE]' as const;

/**
 * Async generator that reads a ReadableStream of SSE frames and yields
 * each parsed JSON event. Handles buffering, decoding, and frame splitting.
 * Yields `null` when the server sends the `[DONE]` signal.
 */
export async function* parseSSEStream(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<Record<string, unknown> | null> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    let streamFinished = false;
    while (!streamFinished) {
      const chunk = await reader.read();
      streamFinished = chunk.done;

      buffer += decoder.decode(chunk.value ?? new Uint8Array(), {
        stream: !streamFinished,
      });

      // Split buffer by double newline to identify separate SSE frames
      const parts = buffer.split(/\r?\n\r?\n/);
      // The last part might be incomplete, so keep it in the buffer
      buffer = streamFinished ? '' : (parts.pop() ?? '');

      for (const part of parts) {
        const match = part.trim().match(/^data:\s*(.*)$/m);
        if (!match) continue;

        const dataStr = match[1].trim();
        if (dataStr === '') continue;

        if (dataStr === DONE_SIGNAL) {
          yield null;
          return;
        }

        yield JSON.parse(dataStr);
      }
    }
  } finally {
    reader.cancel().catch(() => {});
  }
}
